<script setup lang="ts">
import merge from 'lodash-es/merge';

import type { ListItemDetailsModel } from '#layers/prawopl/app/pages/cms/modules/wk-products/_runtime/services/ProductsService';
import ProductsService from '#layers/prawopl/app/pages/cms/modules/wk-products/_runtime/services/ProductsService';

defineOptions({
    name: 'WkProductsSettings'
});

const emit = defineEmits<{
    (e: 'update:modelValue',): void;
}>();

const props = defineProps<{
    modelValue: any;
}>();

const defaults = ref<any>({
    quantity: 5,
    isRandomDisplayEnabled: false,
    products: []
});
const productId = ref<number>(null);
const products = ref<ListItemDetailsModel[]>([]);

const form = computed<any>(() =>
{
    return props.modelValue;
});

watch(() => form.value.settings, () =>
{
    triggerModel();
},
{ deep: true });

function triggerModel(): void
{
    emit('update:modelValue', form.value);
}

async function validate(): Promise<void>
{
    form.value.validate(schema => ({
        settings: schema.object({
            quantity: schema.number().nullable()
                .required($t('[[[Wybranie ilości wyświetlanych pozycji jest wymagane]]]'))
                .typeError($t('[[[Wybranie ilości wyświetlanych pozycji jest wymagane]]]'))
        })
    }));
}

async function fetchProduct(id: number): Promise<ListItemDetailsModel>
{
    const response = await ProductsService.fetchAutocomplete(id);

    return response || null;
}

async function searchProduct(query: string, limit: number): Promise<ListItemDetailsModel[]>
{
    return await ProductsService.getAutocomplete(query, limit);
}

async function addProduct(): Promise<void>
{
    if (productId.value == null)
        return;

    const response = await ProductsService.fetchAutocomplete(productId.value);

    if (products.value.find(x => x.id === response.id) === undefined &&
        form.value.settings.products.find(x => x === response.id) === undefined)
    {
        products.value.push(response);
        form.value.settings.products.push(response.id);
    }

    productId.value = null;
}

function removeProduct(id: number): void
{
    products.value = products.value.filter(x => x.id != id);
    form.value.settings.products = form.value.settings.products.filter(x => x != id);
}

async function loadProducts(id: number): Promise<boolean>
{
    if (id === 0)
        return true;

    try
    {
        products.value = await ProductsService.getListByWidget(id);
    }
    catch (ex)
    {
        return false;
    }

    return true;
}

onBeforeMount(async (): Promise<void> =>
{
    form.value.settings = merge(defaults.value, form.value.settings);
    loadProducts(form.value.id);
});

defineExpose({
    validate
});
</script>

<template>
    <hr>

    <IdeoFormGroup :label="$t('[[[Ilość wyświetlanych pozycji]]]')" :invalid-feedback="form.$errors.first('settings.quantity')" :state="form.$errors.state('settings.quantity')" required>
        <IdeoFormInput v-model="form.settings.quantity" name="quantity" type="number" max="10" min="3" />
    </IdeoFormGroup>

    <IdeoFormGroup>
        <IdeoFormCheckbox v-model="form.settings.isRandomDisplayEnabled">
            {{ $t('[[[Pozycje zostaną wyświetlone losowo ze wszystkich dostępnych]]]') }}
        </IdeoFormCheckbox>
    </IdeoFormGroup>

    <hr>

    <IdeoFormGroup :label="$t('[[[Wybierz produkt]]]')">
        <div class="d-flex flex-column flex-md-row">
            <div class="flex-grow-1">
                <IdeoAutocomplete
                    v-model="productId"
                    name="productId"
                    :limit="100"
                    track-by="id"
                    :placeholder="$t('[[[zacznij pisać aby wyszukać produkt...]]]')"
                    :fetch="(id: number) => fetchProduct(id)"
                    :search="(query: string, limit: number) => searchProduct(query, limit)"
                >
                    <template #selected="{ option }">
                        <div class="text-wrap">
                            <span v-if="option.publicationVersion !== ''">[{{ option.publicationVersion }}] </span>
                            <b>{{ option.name }}</b>
                            [{{ option.price }}zł, {{ option.format || 'bd.' }}, {{ option.coverType || 'bd.' }}, {{ option.yearOfPublication || 'bd.' }}, {{ option.state || 'bd.' }}]
                        </div>
                    </template>
                    <template #option="{ option }">
                        <div class="text-wrap">
                            <span v-if="option.publicationVersion !== ''">[{{ option.publicationVersion }}] </span>
                            <b>{{ option.name }}</b>
                            [{{ option.price }}zł, {{ option.format || 'bd.' }}, {{ option.coverType || 'bd.' }}, {{ option.yearOfPublication || 'bd.' }}, {{ option.state || 'bd.' }}]
                        </div>
                    </template>
                </IdeoAutocomplete>
            </div>
            <div class="d-flex align-items-stretch">
                <button class="btn btn-secondary text-nowrap mt-2 mt-md-0 ms-md-2" @click.prevent="addProduct()">
                    <i class="fas fa-plus" />
                </button>
            </div>
        </div>
    </IdeoFormGroup>

    <IdeoFormGroup :label="$t('[[[Wybrane produkty]]]')">
        <div class="list-group">
            <div v-for="item in products" :key="item.id" class="list-group-item list-group-item-action p-2 d-flex">
                <span class="me-2">
                    <i class="fad fa-fw fa-browser text-primary" />
                </span>
                <span class="me-auto">
                    <span class="d-block" :title="$t('[[[Powiązany produkt]]]')">
                        <span v-if="item.publicationVersion !== ''">[{{ item.publicationVersion }}] </span>
                        <b>{{ item.name }}</b>
                        [{{ item.price }}zł, {{ item.format || 'bd.' }}, {{ item.coverType || 'bd.' }}, {{ item.yearOfPublication || 'bd.' }}, {{ item.state || 'bd.' }}]
                    </span>
                </span>
                <span class="d-flex align-items-center">
                    <IdeoButton :id="`btn-delete-product-${item.id}`" variant="danger" size="sm" pill :title="$t('[[[Usuń produkt]]]')">
                        <i class="fas fa-trash-alt" />
                    </IdeoButton>
                    <Confirmation :message="$t('[[[Potwierdzenie usunięcia]]]')" :target="`btn-delete-product-${item.id}`" :value="item.id" @confirm="removeProduct(item.id)" />
                </span>
            </div>
            <div v-if="products.length == 0" class="list-group-item list-group-item-light text-center">
                {{ $t('[[[Brak wybranych produktów]]]') }}
            </div>
        </div>
    </IdeoFormGroup>
    <hr>
</template>
